import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import contentService from '../services/contentService';
import './TrackedPage.css';

import Loading from '../components/Loading';
let _toastId = 0;
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  const add = useCallback((message, type = 'ok') => {
    const id = ++_toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    timers.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timers.current[id];
    }, 3500);
  }, []);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  return { toasts, add };
}

function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="tp-toast-stack">
      {toasts.map(t => (
        <div key={t.id} className={`tp-toast tp-toast--${t.type}`}>
          {t.type === 'ok' ? '✓' : '✗'} {t.message}
        </div>
      ))}
    </div>
  );
}

const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
};

const fmtEng = (n) => {
  if (n == null) return '—';
  return n.toFixed(2) + '%';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};

const TIER_LABELS = { 1: 'Tier 1 · >100k', 2: 'Tier 2 · 10k-100k', 3: 'Tier 3 · <10k' };
const TIER_COLORS = { 1: '#16a34a', 2: '#2563eb', 3: '#9333ea' };

function AvatarWithFallback({ username, url }) {
  const [failed, setFailed] = useState(false);
  const initial = username ? username[0].toUpperCase() : '?';
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={username}
        className="tp-avatar"
        onError={() => setFailed(true)}
        referrerPolicy="no-referrer"
      />
    );
  }
  return <div className="tp-avatar tp-avatar-placeholder">{initial}</div>;
}

export default function TrackedPage() {
  const { toasts, add: addToast } = useToasts();
  const [profiles, setProfiles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [tierFilter, setTierFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [ordering, setOrdering]   = useState('-followers_count');
  // rescrapeState: { [username]: 'loading' | 'ok' | 'error' }
  const [rescrapeState, setRescrapeState] = useState({});

  useEffect(() => {
    load();
  }, [tierFilter, ordering]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { ordering };
      if (tierFilter) params.tier = tierFilter;
      const res = await contentService.getTrackedProfiles(params);
      setProfiles(res.data?.profiles || []);
    } catch (e) {
      setError('Error al cargar perfiles analizados');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return profiles;
    const q = search.toLowerCase();
    return profiles.filter(
      p => p.username.toLowerCase().includes(q) || (p.full_name || '').toLowerCase().includes(q)
    );
  }, [profiles, search]);

  const handleSort = (col) => {
    setOrdering(prev => prev === col ? `-${col}` : col);
  };

  const handleRescrape = useCallback(async (username) => {
    setRescrapeState(prev => ({ ...prev, [username]: 'loading' }));
    try {
      await contentService.rescrapeInfluencer(username);
      setRescrapeState(prev => ({ ...prev, [username]: 'ok' }));
      addToast(`@${username} agregado a la cola de análisis`, 'ok');
      setTimeout(() => setRescrapeState(prev => { const s = { ...prev }; delete s[username]; return s; }), 3000);
    } catch {
      setRescrapeState(prev => ({ ...prev, [username]: 'error' }));
      addToast(`Error al encolar @${username}`, 'err');
      setTimeout(() => setRescrapeState(prev => { const s = { ...prev }; delete s[username]; return s; }), 3000);
    }
  }, [addToast]);

  const SortIcon = ({ col }) => {
    if (ordering === col) return <span className="tp-sort-icon">▲</span>;
    if (ordering === `-${col}`) return <span className="tp-sort-icon">▼</span>;
    return <span className="tp-sort-icon tp-sort-inactive">⇅</span>;
  };

  return (
    <div className="tp-page">
      <ToastStack toasts={toasts} />
      <div className="tp-header">
        <div className="pig-topbar-left">
          <h1 className="pig-topbar-title">
            <i className="bi bi-radar" /> Perfiles Analizados
          </h1>
          <p className="pig-topbar-sub">Cuentas de Instagram monitoreadas y analizadas</p>
        </div>
        <span className="tp-count">{filtered.length} perfiles</span>
      </div>

      {/* Filtros */}
      <div className="tp-controls">
        <input
          className="tp-search"
          type="text"
          placeholder="🔍 Buscar por usuario o nombre..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="tp-tier-filters">
          {[['', 'Todos'], ['1', 'Tier 1 >100k'], ['2', 'Tier 2 10k-100k'], ['3', 'Tier 3 <10k']].map(([val, label]) => (
            <button
              key={val}
              className={`tp-tier-btn ${tierFilter === val ? 'active' : ''}`}
              onClick={() => setTierFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <Loading fullScreen={false} mensaje="Cargando perfiles" />
      ) : error ? (
        <div className="tp-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="tp-empty">No hay perfiles que coincidan.</div>
      ) : (
        <div className="tp-table-wrap">
          <table className="tp-table">
            <thead>
              <tr>
                <th className="tp-th-profile">Perfil</th>
                <th className="tp-th-sortable" onClick={() => handleSort('followers_count')}>
                  Seguidores <SortIcon col="followers_count" />
                </th>
                <th className="tp-cell-num">Siguiendo</th>
                <th className="tp-cell-num">Posts</th>
                <th className="tp-cell-num">Total Likes</th>
                <th className="tp-cell-num">Total Comments</th>
                <th className="tp-cell-num">Total Views</th>
                <th className="tp-cell-num">Engagement</th>
                <th className="tp-cell-num">Avg Likes</th>
                <th className="tp-cell-num">Avg Comentarios</th>
                <th className="tp-cell-num">Avg Views</th>
                <th className="tp-cell-num">Δ Seguidores</th>
                <th>Tier</th>
                <th>Tipo</th>
                <th>Fuente</th>
                <th title="Análisis IA (Claude)">IA</th>
                <th className="tp-cell-num" title="Posts con colaboración (collab)">Collabs</th>
                <th title="Marcas o cuentas con quienes colabora">Co-autores</th>
                <th className="tp-th-sortable" onClick={() => handleSort('last_scraped_at')}>
                  Último análisis <SortIcon col="last_scraped_at" />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="tp-row">
                  <td className="tp-cell-profile">
                    <div className="tp-profile-info">
                      <AvatarWithFallback username={p.username} url={p.profile_pic_url} />
                      <div>
                        <a
                          href={`https://instagram.com/${p.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tp-username"
                        >
                          @{p.username}
                          {p.is_verified && <span className="tp-verified">✓</span>}
                        </a>
                        {p.full_name && <div className="tp-fullname">{p.full_name}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="tp-cell-num">{fmt(p.followers_count)}</td>
                  <td className="tp-cell-num">{fmt(p.following_count)}</td>
                  <td className="tp-cell-num">{fmt(p.media_count)}</td>
                  <td className="tp-cell-num">{fmt(p.total_likes)}</td>
                  <td className="tp-cell-num">{fmt(p.total_comments)}</td>
                  <td className="tp-cell-num">{fmt(p.total_views)}</td>
                  <td className="tp-cell-num">{fmtEng(p.engagement_rate)}</td>
                  <td className="tp-cell-num">{fmt(p.avg_likes)}</td>
                  <td className="tp-cell-num">{fmt(p.avg_comments)}</td>
                  <td className="tp-cell-num">{fmt(p.avg_views)}</td>
                  <td className="tp-cell-num">
                    {p.followers_delta != null
                      ? <span className={p.followers_delta >= 0 ? 'tp-delta-pos' : 'tp-delta-neg'}>
                          {p.followers_delta >= 0 ? '+' : ''}{fmt(p.followers_delta)}
                        </span>
                      : '—'}
                  </td>
                  <td>
                    <span className="tp-tier-badge" style={{ background: TIER_COLORS[p.tier] + '20', color: TIER_COLORS[p.tier] }}>
                      T{p.tier}
                    </span>
                  </td>
                  <td>
                    {p.is_business
                      ? <span className="tp-badge tp-badge-biz">Business</span>
                      : <span className="tp-badge tp-badge-personal">Personal</span>}
                  </td>
                  <td className="tp-cell-source">{p.source || '—'}</td>
                  <td className="tp-cell-ai">
                    {p.ai_analyzed ? (
                      <span
                        className={`tp-ai-badge tp-ai-badge--${p.ai_brand_safety || 'safe'}`}
                        title={[
                          p.ai_category && `Nicho: ${p.ai_category}${p.ai_subcategory ? ' › ' + p.ai_subcategory : ''}`,
                          p.ai_tone && `Tono: ${p.ai_tone}`,
                          p.ai_audience && `Audiencia: ${p.ai_audience}`,
                          p.ai_brand_sectors?.length && `Marcas: ${p.ai_brand_sectors.join(', ')}`,
                          p.ai_keywords?.length && `Keywords: ${p.ai_keywords.slice(0,6).join(', ')}`,
                          p.ai_brand_safety && `Brand safety: ${p.ai_brand_safety}`,
                        ].filter(Boolean).join('\n')}
                      >
                        🤖 {p.ai_category || 'IA'}
                      </span>
                    ) : (
                      <span className="tp-ai-pending" title="Sin análisis IA aún">—</span>
                    )}
                  </td>
                  <td className="tp-cell-num">
                    {p.collab_count > 0
                      ? <span className="tp-collab-badge">{p.collab_count}</span>
                      : '—'}
                  </td>
                  <td className="tp-cell-partners">
                    {p.collab_partners?.length > 0
                      ? p.collab_partners.map(u => (
                          <a
                            key={u}
                            href={`https://instagram.com/${u}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tp-partner-chip"
                          >@{u}</a>
                        ))
                      : '—'}
                  </td>
                  <td className="tp-cell-date">{fmtDate(p.last_scraped_at)}</td>
                  <td className="tp-cell-action">
                    {rescrapeState[p.username] === 'loading' ? (
                      <button className="tp-rescrape-btn tp-rescrape-btn--loading" disabled>
                        <span className="tp-spin">↻</span>
                      </button>
                    ) : rescrapeState[p.username] === 'ok' ? (
                      <button className="tp-rescrape-btn tp-rescrape-btn--ok" disabled>✓</button>
                    ) : rescrapeState[p.username] === 'error' ? (
                      <button className="tp-rescrape-btn tp-rescrape-btn--err" disabled>✗</button>
                    ) : (
                      <button
                        className="tp-rescrape-btn"
                        title="Re-analizar perfil"
                        onClick={() => handleRescrape(p.username)}
                      >↻</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
